import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {Offer} from '../../model/offer/offer';
import {map, Observable} from 'rxjs';
import {OfferStatus} from '../../enum/offer-status';
import {CompactOffer} from '../../model/offer/compact-offer';
import {ProposalUser} from '../../model/proposal/proposalUser';
import {ImageReader} from '../../util/image-reader';
import {PrivateNote} from '../../model/offer/private-note';
import {SortOffer} from '../../model/offer/sort-offer';
import {OfferReadByUser} from '../../model/offer/offer-read-by-user';
import {OfferVersionHistoryEntry} from '../../model/offer/offer-version-history-entry';

@Injectable({
  providedIn: 'root'
})
export class OfferService {

  constructor(private readonly http: HttpClient,
  ) {
  }

  saveOffer(offer: Offer) {
    return this.http.post(environment.backend + '/v1/offers', offer);
  }

  getOffersByAd(proposalId: string): Observable<Offer[]> {
    return this.http.get<Offer[]>(environment.backend + `/v1/offers?proposal_id=${proposalId}`)
      .pipe(map(offers => this.mapOffers(offers)
      ));
  }

  getOffersForSlip(proposalId: string): Observable<CompactOffer[]> {
    return this.http.get<CompactOffer[]>(environment.backend + `/v1/offers/slip?proposal_id=${proposalId}`)
      .pipe(map(offers => {
        const newOffers = [];
        offers.forEach(offer => newOffers.push(new CompactOffer(offer)));
        return newOffers;
      }));
  }


  mapOffers(offers: Object[]): Offer[] {
    const mappedOffers: Offer[] = [];
    offers.forEach((offer: Offer) => {
      let mappedOffer = new Offer(offer);
      mappedOffer.creator = new ProposalUser(offer.creator);
      mappedOffer.creator.profilePicture = ImageReader.convertImage(offer.creator.profilePicture);
      mappedOffers.push(mappedOffer);
    });
    return mappedOffers;
  }

  acceptOffer(data: {offerId: string, includeCompanyName?: boolean, includeCoverage?: boolean, includeClausesAndConditions?: boolean, includePremium?: boolean, proposalId?: string}): Observable<Offer> {
    const body = {
      status: OfferStatus.ACCEPTED,
      acceptOfferRequest: {
        includeCompanyName: data.includeCompanyName,
        includeCoverage: data.includeCoverage,
        includeClausesAndConditions: data.includeClausesAndConditions,
        includePremium: data.includePremium
      },
      proposalId: data.proposalId
    };
    return this.http.patch<Offer>(environment.backend + `/v1/offers/${data.offerId}`, body);
  }

  retractOffer(offerId: string, proposalId: string): Observable<Offer> {
    return this.http.patch<Offer>(environment.backend + `/v1/offers/${offerId}`, {status: OfferStatus.RETRACTED, proposalId: proposalId});
  }

  refillOffer(offer: Offer): Observable<Offer> {
    return this.http.patch<Offer>(environment.backend + `/v1/offers/${offer.offerId}`, offer );
  }

  savePrivateNote(privateNote: PrivateNote) {
    return this.http.post(environment.backend + '/v1/offers/privateNote', privateNote);
  }

  getPrivateNoteByUserIdAndProposalId(userId: string, proposalId: string): Observable<PrivateNote[]> {
    return this.http.get<PrivateNote[]>(environment.backend + `/v1/offers/privateNotes?user_id=${userId}&proposal_id=${proposalId}`);
  }

  saveOrUpdateSortOrder(sortedOffer: SortOffer) {
    return this.http.post(environment.backend + `/v1/offers/updateSortOrder`, sortedOffer);
  }

  getOfferReadyByUserByProposalAndUser(userId: string, proposalId: string): Observable<OfferReadByUser[]> {
    return this.http.get<OfferReadByUser[]>(environment.backend + `/v1/offers/read-by-user?proposalId=${proposalId}&userId=${userId}`);
  }

  saveOfferReadyByUser(userId: string, proposalId: string, offerId: string) {
    return this.http.post(environment.backend + `/v1/offers/read-by-user`, {proposalId: proposalId, offerId: offerId, userId: userId});
  }

  getOfferHistory(offerId: string): Observable<OfferVersionHistoryEntry[]> {
    return this.http.get<any[]>(environment.backend + `/v1/offers/${offerId}/history`)
      .pipe(map(arr => (arr ?? []).map(entry => new OfferVersionHistoryEntry(entry))));
  }

}
